import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Search, CheckCircle, XCircle, AlertCircle, DollarSign } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache localStorage: 10 minutos por RUT, segmentado por modo (paid/zero).
const MEDILINK_CACHE_TTL_MS = 10 * 60 * 1000;
const cacheKey = (rut, onlyZeroCost = false) =>
  `medilink_lookup:${rut}:${onlyZeroCost ? 'zero' : 'paid'}`;
const getCachedLookup = (rut, onlyZeroCost = false) => {
  try {
    const raw = localStorage.getItem(cacheKey(rut, onlyZeroCost));
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > MEDILINK_CACHE_TTL_MS) {
      localStorage.removeItem(cacheKey(rut, onlyZeroCost));
      return null;
    }
    return data;
  } catch { return null; }
};
const setCachedLookup = (rut, data, onlyZeroCost = false) => {
  try { localStorage.setItem(cacheKey(rut, onlyZeroCost), JSON.stringify({ ts: Date.now(), data })); } catch {}
};

const formatApiError = (error) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(err => err.msg || JSON.stringify(err)).join(', ');
  if (typeof detail === 'object' && detail !== null) return detail.msg || JSON.stringify(detail);
  return error.message || 'Error desconocido';
};

const formatMoney = (amount) => {
  return `$${Number(amount).toLocaleString('es-CL')}`;
};

const PaymentBadge = ({ estado }) => {
  const styles = {
    'Pagado Completo': 'bg-emerald-100 text-emerald-800 border-emerald-200',
    'Abono': 'bg-amber-100 text-amber-800 border-amber-200',
    'Pendiente': 'bg-red-100 text-red-800 border-red-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${styles[estado] || 'bg-gray-100 text-gray-800'}`}>
      {estado}
    </span>
  );
};

export function SurgeryDialog({ open, onClose, surgery, slot }) {
  const [formData, setFormData] = useState({
    patient_name: '',
    patient_rut: '',
    surgery_type: '',
    pavilion: '',
    date: '',
    time: '',
    end_time: '',
    doctor_id: '',
    consultant_id: '',
    state_id: '',
    medilink_confirmed: false,
    transaction_id: '',
    observations: '',
    allergies: '',
    bed: '',
    atencion_id: '',
    atencion_nombre: '',
    atencion_estado_pago: '',
    atencion_total: null,
    atencion_abonado: null,
    atencion_deuda: null,
  });
  const [users, setUsers] = useState([]);
  const [states, setStates] = useState([]);
  const [pavilions, setPavilions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [medilinkStatus, setMedilinkStatus] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [atenciones, setAtenciones] = useState([]);
  // Checkbox 'Refinamiento (costo cero)' — por defecto desactivado.
  // Cuando ON, la búsqueda a Medilink trae SOLO atenciones con monto == 0.
  const [onlyZeroCost, setOnlyZeroCost] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchStates();
    fetchPavilions();
    checkMedilinkStatus();

    if (surgery) {
      // Mezclar con defaults para asegurar que TODAS las llaves existen
      // (Object.assign hace que un campo ausente en el backend no rompa
      // los <Select> controlados).
      setFormData((prev) => ({ ...prev, ...surgery }));
      // Si la cirugía ya tiene una atención linkeada, precargamos la lista
      // para que el usuario vea la atención seleccionada.
      if (surgery.atencion_id && surgery.atencion_nombre) {
        setAtenciones([{
          id: surgery.atencion_id,
          nombre: surgery.atencion_nombre,
          estado_pago: surgery.atencion_estado_pago,
          total: surgery.atencion_total,
          abonado: surgery.atencion_abonado,
          deuda: surgery.atencion_deuda,
        }]);
      }
    } else if (slot) {
      const date = new Date(slot.start);
      const endDate = new Date(date.getTime() + 60 * 60 * 1000);
      setFormData(prev => ({
        ...prev,
        date: date.toISOString().split('T')[0],
        time: date.toTimeString().slice(0, 5),
        end_time: endDate.toTimeString().slice(0, 5),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surgery, slot]);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      setUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchPavilions = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/pavilions`, { withCredentials: true });
      const active = data.filter((p) => p.is_active !== false);
      setPavilions(active);
      // Solo poner default si estamos creando y no hay pabellón cargado.
      // Uso setState funcional para leer el prev real (no un closure viejo).
      if (!surgery && active.length > 0) {
        setFormData((prev) => (prev.pavilion ? prev : { ...prev, pavilion: active[0].name }));
      }
    } catch (err) {
      console.error('Error fetching pavilions:', err);
    }
  };

  const fetchStates = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/states`, { withCredentials: true });
      setStates(data);
      // Solo default cuando NO estamos editando, y aún respetando cualquier
      // state_id ya cargado (evita pisar el estado de la cirugía si esta
      // llamada resuelve después de setFormData(surgery)).
      if (!surgery && data.length > 0) {
        setFormData((prev) => (prev.state_id ? prev : { ...prev, state_id: data[0].id }));
      }
    } catch (err) {
      console.error('Error fetching states:', err);
    }
  };

  const checkMedilinkStatus = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/medilink/status`, { withCredentials: true });
      setMedilinkStatus(data);
    } catch (err) {
      setMedilinkStatus({ configured: false });
    }
  };

  const retryTimerRef = useRef(null);
  const retryAttemptsRef = useRef(0);
  const debounceTimerRef = useRef(null);
  const userTypedRutRef = useRef(false);
  const MAX_LOOKUP_429_RETRIES = 2;

  const lookupPatient = useCallback(async (rutOverride = null, opts = {}) => {
    const rut = (rutOverride ?? formData.patient_rut ?? '').trim();
    if (!rut) {
      if (!opts.silent) setError('Ingresa el RUT del paciente primero');
      return;
    }
    // 1) Cache local — 10 min
    if (!opts.forceRefresh) {
      const cached = getCachedLookup(rut, onlyZeroCost);
      if (cached && cached.found) {
        setPatientData(cached.patient);
        setAtenciones(cached.atenciones || []);
        setError('');
        setFormData(prev => {
          const merged = { ...prev, patient_name: cached.patient?.nombre || prev.patient_name };
          // Auto-selección SOLO si no hay atención ya linkeada (evita perder
          // el vínculo previo al editar una cirugía).
          const ats = cached.atenciones || [];
          if (!prev.atencion_id && ats.length === 1) {
            const a = ats[0];
            return {
              ...merged,
              atencion_id: a.id.toString(),
              atencion_nombre: a.nombre || `Atención #${a.id}`,
              atencion_estado_pago: a.estado_pago,
              atencion_total: a.total ?? null,
              atencion_abonado: a.abonado ?? null,
              atencion_deuda: a.deuda ?? null,
              medilink_confirmed: a.estado_pago === 'Pagado Completo',
              transaction_id: a.id.toString(),
            };
          }
          return merged;
        });
        return;
      }
    }

    setLookingUp(true);
    setPatientData(null);
    setAtenciones([]);
    setError('');

    try {
      const { data } = await axios.post(
        `${API_URL}/api/medilink/lookup-patient`,
        { patient_rut: rut, only_zero_cost: onlyZeroCost },
        { withCredentials: true }
      );

      if (data.found) {
        setPatientData(data.patient);
        const ats = data.atenciones || [];
        setAtenciones(ats);
        setCachedLookup(rut, data, onlyZeroCost);
        retryAttemptsRef.current = 0;
        setFormData(prev => {
          const merged = { ...prev, patient_name: data.patient.nombre || prev.patient_name };
          // Auto-selección SOLO si aún no hay atención vinculada.
          if (!prev.atencion_id && ats.length === 1) {
            const a = ats[0];
            return {
              ...merged,
              atencion_id: a.id.toString(),
              atencion_nombre: a.nombre || `Atención #${a.id}`,
              atencion_estado_pago: a.estado_pago,
              atencion_total: a.total ?? null,
              atencion_abonado: a.abonado ?? null,
              atencion_deuda: a.deuda ?? null,
              medilink_confirmed: a.estado_pago === 'Pagado Completo',
              transaction_id: a.id.toString(),
            };
          }
          return merged;
        });
      } else if (!opts.silent) {
        setError(data.message || 'Paciente no encontrado en Medilink');
      }
    } catch (err) {
      if (err.response?.status === 429) {
        const retryAfter = Number(err.response.headers?.['retry-after']) || 30;
        if (retryAttemptsRef.current >= MAX_LOOKUP_429_RETRIES) {
          retryAttemptsRef.current = 0;
          toast.error('Medilink sigue saturado. Intenta buscar de nuevo en unos minutos.');
        } else {
          retryAttemptsRef.current += 1;
          toast.warning(
            `Medilink está saturado (rate limit). Reintentando en ${retryAfter}s… (${retryAttemptsRef.current}/${MAX_LOOKUP_429_RETRIES})`,
            { duration: retryAfter * 1000 }
          );
          if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
          retryTimerRef.current = setTimeout(() => lookupPatient(rut, { forceRefresh: true }), retryAfter * 1000);
        }
      } else if (!opts.silent) {
        retryAttemptsRef.current = 0;
        setError(err.response?.data?.detail || 'Error al buscar en Medilink');
      }
    } finally {
      setLookingUp(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.patient_rut, onlyZeroCost]);

  // Debounce 500ms: auto-lookup cuando el usuario deja de tipear el RUT.
  // Solo dispara si el usuario tipeó (no al abrir el modal en modo edición).
  useEffect(() => {
    if (!userTypedRutRef.current) return;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const rut = (formData.patient_rut || '').trim();
    // RUT chileno mínimo: 7 dígitos + guión + dv → 9 chars sin puntos.
    if (rut.length < 8) return;
    debounceTimerRef.current = setTimeout(() => {
      lookupPatient(rut, { silent: true });
    }, 500);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [formData.patient_rut, lookupPatient]);

  useEffect(() => () => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
  }, []);

  const selectAtencion = (atencion) => {
    setFormData(prev => {
      const isSelected = prev.atencion_id === atencion.id.toString();
      if (isSelected) {
        // Desvincular: limpia solo las referencias al vínculo.
        return {
          ...prev,
          atencion_id: '',
          atencion_nombre: '',
          transaction_id: '',
          // Metadata derivada de Medilink: la limpiamos también SOLO si no
          // había una cirugía previamente guardada con datos manuales — para
          // ser consistentes con el flujo clínico.
          atencion_estado_pago: surgery ? prev.atencion_estado_pago : '',
          atencion_total: surgery ? prev.atencion_total : null,
          atencion_abonado: surgery ? prev.atencion_abonado : null,
          atencion_deuda: surgery ? prev.atencion_deuda : null,
          medilink_confirmed: surgery ? prev.medilink_confirmed : false,
        };
      }
      // Vincular: SOLO se añade el vínculo. Los campos de montos/estado se
      // completan únicamente si la cirugía NO tenía ya un valor existente
      // (preserva data manual de fecha, montos, estado, etc.).
      const hasVal = (v) => v !== undefined && v !== null && v !== '';
      return {
        ...prev,
        atencion_id: atencion.id.toString(),
        atencion_nombre: atencion.nombre || prev.atencion_nombre || `Atención #${atencion.id}`,
        transaction_id: atencion.id.toString(),
        atencion_estado_pago: hasVal(prev.atencion_estado_pago) ? prev.atencion_estado_pago : atencion.estado_pago,
        atencion_total:  hasVal(prev.atencion_total)  ? prev.atencion_total  : (atencion.total  ?? null),
        atencion_abonado: hasVal(prev.atencion_abonado) ? prev.atencion_abonado : (atencion.abonado ?? null),
        atencion_deuda:  hasVal(prev.atencion_deuda)  ? prev.atencion_deuda  : (atencion.deuda  ?? null),
        medilink_confirmed: typeof prev.medilink_confirmed === 'boolean' && prev.medilink_confirmed
          ? true
          : atencion.estado_pago === 'Pagado Completo',
      };
    });
  };

  const handleTimeChange = (newTime) => {
    const updated = { ...formData, time: newTime };
    if (newTime && !formData.end_time) {
      const [h, m] = newTime.split(':').map(Number);
      const endH = h + 1;
      updated.end_time = `${String(endH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
    setFormData(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side validation: state_id is required (backend also validates)
    if (!formData.state_id) {
      setError('Debes seleccionar un Estado para la cirugía');
      return;
    }
    if (!formData.pavilion) {
      setError('Debes seleccionar un Pabellón');
      return;
    }

    setLoading(true);
    try {
      if (surgery) {
        await axios.put(`${API_URL}/api/surgeries/${surgery.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/surgeries`, formData, { withCredentials: true });
      }
      onClose();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const doctors = users.filter(u => u.role === 'doctor');
  const consultants = users.filter(u => u.role === 'consultora');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#FFFFFF] border-[#E8E5DD] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#1A1C18]">
            {surgery ? 'Editar Cirugía' : 'Nueva Cirugía'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* RUT + Búsqueda Medilink */}
          <div>
            <Label htmlFor="patient_rut">RUT del Paciente</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="patient_rut"
                data-testid="surgery-patient-rut-input"
                value={formData.patient_rut}
                onChange={(e) => {
                  userTypedRutRef.current = true;
                  setFormData({ ...formData, patient_rut: e.target.value });
                }}
                placeholder="12345678-9"
                required
                className="bg-transparent border-[#E8E5DD] flex-1"
              />
              {medilinkStatus?.configured && (
                <Button
                  type="button"
                  onClick={() => lookupPatient(null, { forceRefresh: true })}
                  disabled={lookingUp || !formData.patient_rut}
                  className="bg-[#4A7C59] hover:bg-[#3A604D] text-white px-4"
                  data-testid="lookup-medilink-button"
                >
                  {lookingUp ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                  <span className="ml-2">Buscar</span>
                </Button>
              )}
            </div>
            {medilinkStatus?.configured && (
              <label className="flex items-center gap-2 mt-2 cursor-pointer select-none" data-testid="refinamiento-toggle-wrapper">
                <input
                  type="checkbox"
                  checked={onlyZeroCost}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setOnlyZeroCost(next);
                    // Al cambiar el modo, limpiamos la lista de atenciones mostradas
                    // y relanzamos la búsqueda si ya hay un RUT válido escrito.
                    setAtenciones([]);
                    const rut = (formData.patient_rut || '').trim();
                    if (rut.length >= 8) {
                      lookupPatient(rut, { silent: true, forceRefresh: true, _mode: next });
                    }
                  }}
                  className="h-3.5 w-3.5 rounded border-[#E8E5DD] text-[#4A7C59] focus:ring-[#4A7C59]"
                  data-testid="refinamiento-costo-cero-checkbox"
                />
                <span className="text-xs text-[#6B6B6B]">
                  Refinamiento (costo cero) — buscar solo atenciones con monto $0
                </span>
              </label>
            )}
          </div>

          {/* Datos del paciente encontrado */}
          {patientData && (
            <div className="p-3 bg-[#F5F3EE] rounded-lg border border-[#E8E5DD]" data-testid="patient-info-card">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="h-4 w-4 text-emerald-600" />
                <span className="font-medium text-sm text-[#1A1C18]">{patientData.nombre}</span>
              </div>
              <div className="text-xs text-[#6B6B6B] space-x-3">
                {patientData.celular && <span>{patientData.celular}</span>}
                {patientData.email && <span>{patientData.email}</span>}
              </div>
            </div>
          )}

          {/* Atenciones con presupuesto */}
          {atenciones.length > 0 && (
            <div data-testid="atenciones-list">
              <Label className="mb-2 block">Atenciones con presupuesto</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {atenciones.map((a) => {
                  const isSelected = formData.atencion_id === a.id.toString();
                  return (
                    <div
                      key={a.id}
                      data-testid={`atencion-item-${a.id}`}
                      onClick={() => selectAtencion(a)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected
                          ? 'border-[#4A7C59] bg-[#4A7C59]/5 ring-1 ring-[#4A7C59]'
                          : 'border-[#E8E5DD] hover:border-[#C4C0B6] bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-[#6B6B6B]">#{a.id}</span>
                            <span className="font-medium text-sm text-[#1A1C18] truncate">
                              {a.nombre || 'Sin nombre'}
                            </span>
                          </div>
                          <div className="text-xs text-[#6B6B6B] mt-1">
                            {a.profesional} &middot; {a.fecha}
                          </div>
                        </div>
                        <PaymentBadge estado={a.estado_pago} />
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs">
                        <span className="text-[#6B6B6B]">Total: <strong className="text-[#1A1C18]">{formatMoney(a.total)}</strong></span>
                        <span className="text-[#6B6B6B]">Abonado: <strong className={a.abonado > 0 ? 'text-emerald-700' : 'text-[#1A1C18]'}>{formatMoney(a.abonado)}</strong></span>
                        {a.deuda > 0 && (
                          <span className="text-[#6B6B6B]">Deuda: <strong className="text-red-700">{formatMoney(a.deuda)}</strong></span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {patientData && atenciones.length === 0 && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <span className="text-sm text-amber-800">No se encontraron atenciones con presupuesto para este paciente</span>
            </div>
          )}

          {/* Nombre paciente */}
          <div>
            <Label htmlFor="patient_name">Nombre del Paciente</Label>
            <Input
              id="patient_name"
              data-testid="surgery-patient-name-input"
              value={formData.patient_name}
              onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
              required
              className="mt-1 bg-transparent border-[#E8E5DD]"
            />
          </div>

          <div>
            <Label htmlFor="surgery_type">Tipo de cirugía</Label>
            <Input
              id="surgery_type"
              data-testid="surgery-type-input"
              value={formData.surgery_type}
              onChange={(e) => setFormData({ ...formData, surgery_type: e.target.value })}
              required
              className="mt-1 bg-transparent border-[#E8E5DD]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date">Fecha</Label>
              <Input
                id="date"
                type="date"
                data-testid="surgery-date-input"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                className="mt-1 bg-transparent border-[#E8E5DD]"
              />
            </div>
            <div>
              <Label htmlFor="pavilion">Pabellón</Label>
              <Select
                value={formData.pavilion}
                onValueChange={(value) => setFormData({ ...formData, pavilion: value })}
              >
                <SelectTrigger data-testid="surgery-pavilion-select" className="mt-1 bg-transparent border-[#E8E5DD]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pavilions.map((p) => (
                    <SelectItem key={p.id} value={p.name} data-testid={`surgery-pavilion-option-${p.name}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="time">Hora Inicio</Label>
              <Input
                id="time"
                type="time"
                data-testid="surgery-time-input"
                value={formData.time}
                onChange={(e) => handleTimeChange(e.target.value)}
                required
                className="mt-1 bg-transparent border-[#E8E5DD]"
              />
            </div>
            <div>
              <Label htmlFor="end_time">Hora Término</Label>
              <Input
                id="end_time"
                type="time"
                data-testid="surgery-end-time-input"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                required
                className="mt-1 bg-transparent border-[#E8E5DD]"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="doctor_id">Doctor</Label>
              <Select
                value={formData.doctor_id}
                onValueChange={(value) => setFormData({ ...formData, doctor_id: value })}
              >
                <SelectTrigger data-testid="surgery-doctor-select" className="mt-1 bg-transparent border-[#E8E5DD]">
                  <SelectValue placeholder="Seleccionar doctor" />
                </SelectTrigger>
                <SelectContent>
                  {doctors.map(doc => (
                    <SelectItem key={doc.id} value={doc.id}>{doc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="consultant_id">Consultora</Label>
              <Select
                value={formData.consultant_id}
                onValueChange={(value) => setFormData({ ...formData, consultant_id: value })}
              >
                <SelectTrigger data-testid="surgery-consultant-select" className="mt-1 bg-transparent border-[#E8E5DD]">
                  <SelectValue placeholder="Seleccionar consultora" />
                </SelectTrigger>
                <SelectContent>
                  {consultants.map(cons => (
                    <SelectItem key={cons.id} value={cons.id}>{cons.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="state_id">Estado</Label>
            <Select
              value={formData.state_id}
              onValueChange={(value) => setFormData({ ...formData, state_id: value })}
            >
              <SelectTrigger data-testid="surgery-state-select" className="mt-1 bg-transparent border-[#E8E5DD]">
                <SelectValue placeholder="Seleccionar estado" />
              </SelectTrigger>
              <SelectContent>
                {states.map(state => (
                  <SelectItem key={state.id} value={state.id}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: state.color }}></div>
                      {state.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Estado de pago Medilink */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-[#E8E5DD]">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-[#6B6B6B]" />
              <Label htmlFor="medilink_confirmed" className="cursor-pointer">
                Pago confirmado en Medilink
              </Label>
              {formData.atencion_estado_pago && (
                <PaymentBadge estado={formData.atencion_estado_pago} />
              )}
            </div>
            <Switch
              id="medilink_confirmed"
              data-testid="surgery-medilink-confirmed-switch"
              checked={formData.medilink_confirmed}
              onCheckedChange={(checked) => setFormData({ ...formData, medilink_confirmed: checked })}
            />
          </div>

          {formData.atencion_id && (
            <div className="text-xs text-[#6B6B6B] px-1">
              Atención vinculada: #{formData.atencion_id} - {formData.atencion_nombre}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="allergies">Alergias del paciente <span className="text-xs text-[#6A736B] font-normal">(opcional)</span></Label>
              <Textarea
                id="allergies"
                data-testid="surgery-allergies-input"
                value={formData.allergies || ''}
                onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                className="mt-1 bg-transparent border-[#E8E5DD]"
                rows={2}
                placeholder="Ej: Penicilina, látex, iodo…"
              />
            </div>
            <div>
              <Label htmlFor="bed">Cama <span className="text-xs text-[#6A736B] font-normal">(opcional)</span></Label>
              <Input
                id="bed"
                data-testid="surgery-bed-input"
                value={formData.bed || ''}
                onChange={(e) => setFormData({ ...formData, bed: e.target.value })}
                className="mt-1 bg-transparent border-[#E8E5DD]"
                placeholder="Ej: Cama 3, Recuperación A"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="observations">Observaciones</Label>
            <Textarea
              id="observations"
              data-testid="surgery-observations-input"
              value={formData.observations}
              onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
              className="mt-1 bg-transparent border-[#E8E5DD]"
              rows={3}
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              data-testid="submit-surgery-button"
              disabled={loading}
              className="bg-[#2B4C3B] hover:bg-[#3A604D] text-white"
            >
              {loading ? 'Guardando...' : surgery ? 'Actualizar' : 'Agendar Cirugía'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
